/**
 * Movement Utilities for D&D 5e Battle Room
 *
 * Provides utility functions for token placement, movement tracking, and turn-based movement allocation.
 * Integrates with MovementEngine.ts for comprehensive movement validation.
 *
 * @date_of_change 2025-09-19 - Initial implementation for comprehensive movement system
 */

import ActionEconomyManager from './ActionEconomyManager';
import { MovementEngine } from '../movement/MovementEngine';
import { areCombatAllies } from './combat/CombatRelationships';

// WHAT: treat absent/null grid coordinates as absent instead of numeric zero.
// WHY: realtime and legacy snapshots may carry grid: null; coercing that value
// to 0 makes unrelated actors appear to occupy the map origin during placement.
// date_of_change: 2026-08-25
const finiteGridNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * Finds the nearest unoccupied grid space to a target position.
 * Used when adding tokens or when effects teleport/move tokens to occupied spaces.
 *
 * @param {Object} targetPosition - Desired position {x, y} in grid coordinates
 * @param {Array} tokens - Array of all tokens on the battlefield
 * @param {number} gridSize - Size of grid squares in pixels (default 50)
 * @param {number} maxSearchRadius - Maximum search radius in grid squares (default 10)
 * @param {number} tokenSize - Size of the token being placed in grid squares (default 1)
 * @returns {Object} Nearest unoccupied position {x, y} in grid coordinates
 */
export function findNearestUnoccupiedSpace(
  targetPosition,
  tokens,
  gridSize = 50,
  maxSearchRadius = 10,
  tokenSize = 1,
  searchOptions = {}
) {
  const { x: targetX, y: targetY } = targetPosition;

  // Check if target position is already unoccupied
  if (isSpaceUnoccupied(targetPosition, tokens, gridSize, tokenSize, searchOptions)) {
    return targetPosition;
  }

  // Search in expanding rings around the target position
  for (let radius = 1; radius <= maxSearchRadius; radius++) {
    // Check all positions at this radius using a diamond pattern
    const positions = getPositionsAtRadius(targetX, targetY, radius);

    for (const position of positions) {
      if (isSpaceUnoccupied(position, tokens, gridSize, tokenSize, searchOptions)) {
        return position;
      }
    }
  }

  // Fallback: return a position far away if no space found
  console.warn(`[MovementUtils] No unoccupied space found within ${maxSearchRadius} squares of target position`, targetPosition);
  const bounds = searchOptions?.bounds;
  if (bounds) {
    return {
      x: Math.min(Math.max(targetX, bounds.minX ?? 0), bounds.maxX ?? targetX),
      y: Math.min(Math.max(targetY, bounds.minY ?? 0), bounds.maxY ?? targetY)
    };
  }
  return { x: targetX + maxSearchRadius + 1, y: targetY + maxSearchRadius + 1 };
}

/**
 * Builds a presentation-aware placement policy for roster and library tokens.
 * WHAT: reserve screen-readable nameplate space and keep the search on-map.
 * WHY: collision-free adjacent grid cells still render as one unusable badge
 * pile because token nameplates remain a fixed screen size while the map zooms.
 * date_of_change: 2026-08-25
 */
export function buildReadableTokenPlacementSearch({
  gridSize = 50,
  viewScale,
  gridWidth,
  gridHeight,
  tokenSize = 1
} = {}) {
  const normalizedGridSize = Number(gridSize);
  const normalizedViewScale = Number(viewScale);
  const columns = Number(gridWidth);
  const rows = Number(gridHeight);
  const normalizedTokenSize = Math.max(Number(tokenSize) || 1, 0.5);

  if (
    !Number.isFinite(normalizedGridSize) || normalizedGridSize <= 0 ||
    !Number.isFinite(normalizedViewScale) || normalizedViewScale <= 0
  ) {
    return { maxSearchRadius: 10, searchOptions: {} };
  }

  const screenCellSize = normalizedGridSize * normalizedViewScale;
  const horizontalClearance = Math.max(0, Math.ceil(180 / screenCellSize) - Math.ceil(normalizedTokenSize));
  const verticalClearance = Math.max(0, Math.ceil(24 / screenCellSize) - Math.ceil(normalizedTokenSize));
  const hasFiniteBounds = Number.isFinite(columns) && columns > 0 && Number.isFinite(rows) && rows > 0;

  return {
    maxSearchRadius: hasFiniteBounds ? Math.max(10, Math.ceil(columns), Math.ceil(rows)) : 10,
    searchOptions: {
      clearanceCells: { x: horizontalClearance, y: verticalClearance },
      ...(hasFiniteBounds ? {
        bounds: {
          minX: 0,
          minY: 0,
          maxX: Math.max(0, Math.floor(columns - normalizedTokenSize)),
          maxY: Math.max(0, Math.floor(rows - normalizedTokenSize))
        }
      } : {})
    }
  };
}

/**
 * Checks if a space is unoccupied by other tokens.
 * Considers token size when checking for overlaps.
 *
 * @param {Object} position - Position to check {x, y} in grid coordinates
 * @param {Array} tokens - Array of all tokens
 * @param {number} gridSize - Size of grid squares in pixels
 * @param {number} tokenSize - Size of token in grid squares
 * @returns {boolean} True if space is unoccupied
 */
function isSpaceUnoccupied(position, tokens, gridSize = 50, tokenSize = 1, searchOptions = {}) {
  const { x, y } = position;
  const bounds = searchOptions?.bounds;
  if (
    bounds && (
      x < (bounds.minX ?? 0) ||
      y < (bounds.minY ?? 0) ||
      x > (bounds.maxX ?? x) ||
      y > (bounds.maxY ?? y)
    )
  ) {
    return false;
  }

  const clearanceX = Math.max(0, Number(searchOptions?.clearanceCells?.x) || 0);
  const clearanceY = Math.max(0, Number(searchOptions?.clearanceCells?.y) || 0);

  if (clearanceX > 0 || clearanceY > 0) {
    const candidateRight = x + tokenSize;
    const candidateBottom = y + tokenSize;

    for (const token of tokens) {
      if (token.type === 'map') continue;

      const nestedGridX = finiteGridNumber(token?.grid?.x);
      const nestedGridY = finiteGridNumber(token?.grid?.y);
      const legacyGridX = finiteGridNumber(token?.gridX);
      const legacyGridY = finiteGridNumber(token?.gridY);
      const tokenX = nestedGridX !== null && nestedGridY !== null
        ? nestedGridX
        : (legacyGridX !== null && legacyGridY !== null
          ? legacyGridX
          : (Number(token?.x) || 0) / gridSize);
      const tokenY = nestedGridX !== null && nestedGridY !== null
        ? nestedGridY
        : (legacyGridX !== null && legacyGridY !== null
          ? legacyGridY
          : (Number(token?.y) || 0) / gridSize);
      const existingSize = token.width && token.height
        ? Math.max(Number(token.width) / gridSize, Number(token.height) / gridSize)
        : ({ Tiny: 0.5, Small: 1, Medium: 1, Large: 2, Huge: 3, Gargantuan: 4 }[token.size] || 1);
      const existingRight = tokenX + existingSize;
      const existingBottom = tokenY + existingSize;
      const horizontallySeparated = candidateRight + clearanceX <= tokenX || existingRight + clearanceX <= x;
      const verticallySeparated = candidateBottom + clearanceY <= tokenY || existingBottom + clearanceY <= y;

      if (!horizontallySeparated && !verticallySeparated) {
        return false;
      }
    }

    return true;
  }

  // Check each square that the token would occupy
  for (let dx = 0; dx < tokenSize; dx++) {
    for (let dy = 0; dy < tokenSize; dy++) {
      const checkX = x + dx;
      const checkY = y + dy;

      // Check if any existing token occupies this square
      for (const token of tokens) {
        if (token.type === 'map') continue; // Map assets don't block movement

        // Match Token.js rendering: nested token.grid is canonical when present, then legacy
        // gridX/gridY, then pixel x/y. date_of_change: 2026-06-23
        const nestedGridX = finiteGridNumber(token?.grid?.x);
        const nestedGridY = finiteGridNumber(token?.grid?.y);
        const legacyGridX = finiteGridNumber(token?.gridX);
        const legacyGridY = finiteGridNumber(token?.gridY);
        let tokenX;
        let tokenY;
        if (nestedGridX !== null && nestedGridY !== null) {
          tokenX = Math.round(nestedGridX);
          tokenY = Math.round(nestedGridY);
        } else if (legacyGridX !== null && legacyGridY !== null) {
          tokenX = Math.round(legacyGridX);
          tokenY = Math.round(legacyGridY);
        } else {
          tokenX = Math.round((Number(token?.x) || 0) / gridSize);
          tokenY = Math.round((Number(token?.y) || 0) / gridSize);
        }

        // Calculate token size in grid squares from pixel dimensions or size category
        let existingSize = 1; // Default to Medium (1x1)
        if (token.width && token.height) {
          // Use actual pixel dimensions if available
          existingSize = Math.max(Math.round(token.width / gridSize), Math.round(token.height / gridSize));
        } else if (token.size) {
          // Convert size category to grid squares
          const sizeCategories = {
            'Tiny': 0.5,
            'Small': 1,
            'Medium': 1,
            'Large': 2,
            'Huge': 3,
            'Gargantuan': 4
          };
          existingSize = sizeCategories[token.size] || 1;
        }

        // Check if this token overlaps with the square we're checking
        for (let tx = 0; tx < existingSize; tx++) {
          for (let ty = 0; ty < existingSize; ty++) {
            if (tokenX + tx === checkX && tokenY + ty === checkY) {
              return false; // Space is occupied
            }
          }
        }
      }
    }
  }

  return true; // Space is unoccupied
}

/**
 * Gets all grid positions at a specific radius from a center point.
 * Uses a diamond pattern for D&D-appropriate movement.
 *
 * @param {number} centerX - Center X coordinate
 * @param {number} centerY - Center Y coordinate
 * @param {number} radius - Radius in grid squares
 * @returns {Array} Array of positions {x, y}
 */
function getPositionsAtRadius(centerX, centerY, radius) {
  const positions = [];

  // Diamond pattern: |dx| + |dy| = radius
  for (let dx = -radius; dx <= radius; dx++) {
    const dyLimit = radius - Math.abs(dx);
    for (let dy = -dyLimit; dy <= dyLimit; dy += dyLimit === 0 ? 1 : dyLimit * 2) {
      positions.push({ x: centerX + dx, y: centerY + dy });
    }
  }

  return positions;
}

/**
 * Turn-based movement tracker for D&D 5e combat.
 * Tracks movement allocation, Dash actions, and movement restrictions per turn.
 */
export class TurnMovementTracker {
  constructor() {
    this.turnData = new Map(); // tokenId -> movement data for current turn
    this.currentTurn = null;
    this.gridSize = 5; // feet per square
  }

  /**
   * Starts a new turn for a token, resetting movement allocation.
   * @param {string} tokenId - ID of the token whose turn is starting
   * @param {Object} tokenData - Token data including speed, conditions, class features
   */
  startTurn(tokenId, tokenData) {
    this.currentTurn = tokenId;

    // Get base movement speed
    const baseSpeed = this.getBaseSpeed(tokenData);
    // A reconnect is not a new turn. Seed the transient validator from the
    // persisted token snapshot instead of silently refunding movement or Dash.
    // date_of_change: 2026-07-18
    const persistedMovementUsed = Number(tokenData?.combatFlags?.movementUsed);
    const usedMovement = Number.isFinite(persistedMovementUsed)
      ? Math.max(0, persistedMovementUsed)
      : 0;
    const dashUsed = tokenData?.combatFlags?.dashUsedThisTurn === true;
    const movementLocked = tokenData?.combatFlags?.movementLockedThisTurn === true;

    // Initialize movement data for this turn
    this.turnData.set(tokenId, {
      baseSpeed,
      totalMovement: dashUsed ? baseSpeed * 2 : baseSpeed,
      usedMovement,
      startPosition: { x: tokenData.x, y: tokenData.y },
      currentPosition: { x: tokenData.x, y: tokenData.y },
      dashUsed,
      bonusDashUsed: dashUsed && tokenData?.combatFlags?.bonusActionUsed === true,
      movementLocked,
      movementLockReason: movementLocked
        ? (tokenData?.combatFlags?.movementLockReason || 'Movement is unavailable for the rest of this turn')
        : null,
      movements: [], // Track individual movements this turn
      conditions: tokenData.conditions || [],
      restrictions: this.calculateMovementRestrictions(tokenData)
    });

    console.log(`[TurnMovementTracker] Started turn for ${tokenId}:`, this.turnData.get(tokenId));
  }

  /**
   * Rehydrate the transient drag validator from the token snapshot rendered by
   * the action panel. Turn-start notifications can arrive before the service
   * token reset, so startTurn alone is not sufficient on reconnect or a round
   * boundary. The canonical token must be able to lower stale movementUsed as
   * well as increase it after a committed move.
   * date_of_change: 2026-07-18
   */
  synchronizeTurnSnapshot(tokenId, tokenData) {
    const flags = tokenData?.combatFlags;
    const hasCanonicalMovement = flags && typeof flags === 'object'
      && Object.prototype.hasOwnProperty.call(flags, 'movementUsed');

    if (!this.turnData.has(tokenId)) {
      this.startTurn(tokenId, tokenData);
      return this.getMovementSummary(tokenId);
    }

    const turnData = this.turnData.get(tokenId);
    if (!hasCanonicalMovement) {
      // Some older/live snapshots omit combatFlags entirely, but their grid
      // position is still canonical. Reconcile position without inferring a
      // new allowance or refunding an existing turn budget.
      // date_of_change: 2026-07-26
      turnData.currentPosition = { x: tokenData.x, y: tokenData.y };
      turnData.conditions = tokenData.conditions || [];
      turnData.restrictions = this.calculateMovementRestrictions(tokenData);
      return this.getMovementSummary(tokenId);
    }

    const baseSpeed = this.getBaseSpeed(tokenData);
    const usedMovement = Math.max(0, Number(flags.movementUsed) || 0);
    const dashUsed = flags.dashUsedThisTurn === true;
    const movementLocked = flags.movementLockedThisTurn === true;

    turnData.baseSpeed = baseSpeed;
    turnData.totalMovement = dashUsed ? baseSpeed * 2 : baseSpeed;
    turnData.usedMovement = usedMovement;
    turnData.currentPosition = { x: tokenData.x, y: tokenData.y };
    turnData.dashUsed = dashUsed;
    turnData.bonusDashUsed = dashUsed && flags.bonusActionUsed === true;
    turnData.movementLocked = movementLocked;
    turnData.movementLockReason = movementLocked
      ? (flags.movementLockReason || 'Movement is unavailable for the rest of this turn')
      : null;
    turnData.conditions = tokenData.conditions || [];
    turnData.restrictions = this.calculateMovementRestrictions(tokenData);

    return this.getMovementSummary(tokenId);
  }

  /**
   * Validates and executes a movement for the current turn.
   * @param {string} tokenId - ID of token moving
   * @param {Object} newPosition - Target position {x, y}
   * @param {Object} battlefield - Current battlefield state
   * @param {Object} options - Movement options (forcedMovement, etc.)
   * @returns {Object} Movement result with success/failure and remaining movement
   */
  executeMovement(tokenId, newPosition, battlefield, options = {}) {
    const turnData = this.turnData.get(tokenId);
    if (!turnData) {
      return { success: false, error: 'No active turn for this token' };
    }
    // The GM override may bypass a turn-budget lock, but MovementEngine still
    // enforces actual zero speed, conditions, path geometry, and reactions.
    // date_of_change: 2026-09-07
    if (turnData.movementLocked && !options.forcedMovement && !options.ignoreActionEconomy) {
      return { success: false, error: turnData.movementLockReason || 'Movement is unavailable for the rest of this turn' };
    }

    // Calculate movement path and cost
    const path = MovementEngine.calculateOptimalPath(
      turnData.currentPosition,
      newPosition,
      battlefield
    );

    // date_of_change: 2026-06-07
    // WHAT: Reconcile Dash before MovementEngine validates the path against speed.
    // WHY: D&D 5e Dash increases the movement available for the current turn; validating
    // a dashed rogue against only base walking speed blocks legal post-Dash movement.
    this.reconcileActionEconomyMovement(tokenId, turnData);

    // Get applicable speeds based on token data, conditions, and the turn's Dash-adjusted budget.
    const speeds = this.getMovementSpeeds(tokenId, turnData);

    // Validate movement using MovementEngine
    const validation = MovementEngine.validateMovement(
      tokenId,
      path,
      battlefield,
      speeds,
      options
    );

    if (!validation.ok) {
      return {
        success: false,
        error: validation.errors?.join(', ') || 'Movement validation failed',
        warnings: validation.warnings
      };
    }

    // Cunning Strike: Withdraw grants one immediate, attack-linked movement
    // allowance that does not spend ordinary movement and does not provoke
    // from the struck target. The canonical pending flag is consumed by the
    // BattleRoom in the same position commit.
    // rule_reference: 2024 PHB Rogue — Cunning Strike: Withdraw.
    // date_of_change: 2026-07-18
    const specialMovementAllowance = Number(options.specialMovementAllowance);
    const usesSpecialMovement = Number.isFinite(specialMovementAllowance) && specialMovementAllowance >= 0;
    if (usesSpecialMovement && validation.spent > specialMovementAllowance) {
      return {
        success: false,
        error: `Cunning Strike Withdraw allows ${specialMovementAllowance} ft; this move requires ${validation.spent} ft`,
        availableMovement: specialMovementAllowance,
        requiredMovement: validation.spent
      };
    }

    // Check if we have enough ordinary movement remaining.
    const availableMovement = Math.max(0, turnData.totalMovement - turnData.usedMovement);

    if (!usesSpecialMovement && !options.ignoreActionEconomy && validation.spent > availableMovement) {
      return {
        success: false,
        error: `Insufficient movement: need ${validation.spent} ft, have ${availableMovement} ft remaining`,
        availableMovement,
        requiredMovement: validation.spent
      };
    }

    // Execute the movement
    if (!usesSpecialMovement) turnData.usedMovement += validation.spent;
    turnData.currentPosition = newPosition;
    turnData.movements.push({
      from: path.points[0],
      to: newPosition,
      cost: validation.spent,
      path: validation.path,
      triggers: validation.triggers,
      specialMovement: usesSpecialMovement ? 'cunning_strike_withdraw' : null
    });

    // The tracker validates and calculates movement, but does not write a second
    // action-economy record. BattleRoom commits position + combatFlags.movementUsed
    // atomically, and ActionTracker projects that canonical token snapshot.
    // date_of_change: 2026-07-18

    const resolvedTriggers = usesSpecialMovement && options.ignoreOpportunityAttackFrom
      ? {
        ...validation.triggers,
        opportunityAttacks: (validation.triggers?.opportunityAttacks || []).filter((id) => (
          String(id) !== String(options.ignoreOpportunityAttackFrom)
        ))
      }
      : validation.triggers;

    return {
      success: true,
      spent: validation.spent,
      remaining: Math.max(0, turnData.totalMovement - turnData.usedMovement),
      triggers: resolvedTriggers,
      specialMovement: usesSpecialMovement ? 'cunning_strike_withdraw' : null,
      specialMovementRemaining: usesSpecialMovement ? Math.max(0, specialMovementAllowance - validation.spent) : null,
      warnings: [
        ...(validation.warnings || []),
        ...(options.ignoreActionEconomy ? ['GM action-economy override: normal movement budget ignored.'] : [])
      ]
    };
  }

  /**
   * Uses Dash action to double movement speed.
   * @param {string} tokenId - ID of token using Dash
   * @param {boolean} isBonusAction - True if using bonus action (rogue feature)
   * @returns {Object} Result of Dash action
   */
  useDash(tokenId, isBonusAction = false) {
    const turnData = this.turnData.get(tokenId);
    if (!turnData) {
      return { success: false, error: 'No active turn for this token' };
    }
    if (turnData.movementLocked) {
      return { success: false, error: turnData.movementLockReason || 'Movement is unavailable for the rest of this turn' };
    }

    // Check if already used appropriate Dash type
    if (isBonusAction && turnData.bonusDashUsed) {
      return { success: false, error: 'Bonus action Dash already used this turn' };
    }
    if (!isBonusAction && turnData.dashUsed) {
      return { success: false, error: 'Action Dash already used this turn' };
    }

    // Add movement equal to base speed
    const additionalMovement = turnData.baseSpeed;
    turnData.totalMovement += additionalMovement;

    if (isBonusAction) {
      turnData.bonusDashUsed = true;
    } else {
      turnData.dashUsed = true;
    }

    console.log(`[TurnMovementTracker] ${isBonusAction ? 'Bonus' : 'Action'} Dash used by ${tokenId}, added ${additionalMovement} ft movement`);

    return {
      success: true,
      additionalMovement,
      totalMovement: turnData.totalMovement,
      remaining: turnData.totalMovement - turnData.usedMovement
    };
  }

  /**
   * Gets movement summary for a token's current turn.
   * @param {string} tokenId - Token to get data for
   * @returns {Object} Movement summary
   */
  getMovementSummary(tokenId) {
    const turnData = this.turnData.get(tokenId);
    if (!turnData) {
      return null;
    }

    return {
      baseSpeed: turnData.baseSpeed,
      totalMovement: turnData.totalMovement,
      usedMovement: turnData.usedMovement,
      remainingMovement: Math.max(0, turnData.totalMovement - turnData.usedMovement),
      dashUsed: turnData.dashUsed,
      bonusDashUsed: turnData.bonusDashUsed,
      movementLocked: Boolean(turnData.movementLocked),
      movementLockReason: turnData.movementLockReason || null,
      movementCount: turnData.movements.length,
      restrictions: turnData.restrictions
    };
  }

  /**
   * Capture/restore the transient rules calculation around canonical persistence.
   * If the token batch cannot be committed, rollback prevents the tracker from
   * retaining movement that the session snapshot rejected.
   * date_of_change: 2026-07-18
   */
  createCheckpoint(tokenId) {
    const turnData = this.turnData.get(tokenId);
    if (!turnData) return null;
    return JSON.parse(JSON.stringify(turnData));
  }

  restoreCheckpoint(tokenId, checkpoint) {
    if (!checkpoint) {
      this.turnData.delete(tokenId);
      return;
    }
    this.turnData.set(tokenId, JSON.parse(JSON.stringify(checkpoint)));
  }

  lockMovement(tokenId, reason = 'Movement is unavailable for the rest of this turn') {
    const turnData = this.turnData.get(tokenId);
    if (!turnData) return { success: false, error: 'No active turn for this token' };
    if (Number(turnData.usedMovement || 0) > 0) {
      return { success: false, error: 'Movement was already used this turn' };
    }
    turnData.totalMovement = 0;
    turnData.movementLocked = true;
    turnData.movementLockReason = reason;
    return { success: true, remaining: 0 };
  }

  reconcileActionEconomyMovement(tokenId, turnData) {
    const summary = ActionEconomyManager.getActionSummary(tokenId, turnData.baseSpeed);
    const economyAvailable = Number(summary?.movement?.available);
    const economyTotal = Number(summary?.movement?.total);
    const trackerAvailable = turnData.totalMovement - turnData.usedMovement;

    if (Number.isFinite(economyAvailable) && economyAvailable > trackerAvailable) {
      turnData.totalMovement = Math.max(
        turnData.totalMovement,
        Number.isFinite(economyTotal) ? economyTotal : turnData.usedMovement + economyAvailable
      );
    }

    return turnData;
  }

  /**
   * Calculates base movement speed from token data.
   * @param {Object} tokenData - Token data including speed and conditions
   * @returns {number} Base movement speed in feet
   */
  getBaseSpeed(tokenData) {
    // Handle different speed data formats
    let baseSpeed = 30; // Default human speed

    if (typeof tokenData.speed === 'number') {
      baseSpeed = tokenData.speed;
    } else if (typeof tokenData.speed === 'object' && tokenData.speed.walk) {
      baseSpeed = tokenData.speed.walk;
    } else if (tokenData.character?.speed) {
      baseSpeed = tokenData.character.speed;
    }

    // Apply conditions that affect movement
    const conditions = tokenData.conditions || [];
    if (conditions.includes('grappled') || conditions.includes('restrained')) {
      baseSpeed = 0;
    } else if (conditions.includes('slowed')) {
      baseSpeed = Math.floor(baseSpeed / 2);
    }

    return baseSpeed;
  }

  /**
   * Gets all available movement speeds for a token.
   * @param {string} tokenId - Token ID
   * @param {Object} turnData - Turn data for the token
   * @returns {Object} Movement speeds object for MovementEngine
   */
  getMovementSpeeds(tokenId, turnData) {
    const walkBudget = Math.max(turnData.baseSpeed, turnData.totalMovement);
    // This would integrate with character data to get fly, swim, climb speeds
    return {
      walk: walkBudget,
      fly: 0, // Would be extracted from character abilities
      swim: Math.floor(walkBudget / 2), // Default swim speed
      climb: Math.floor(walkBudget / 2) // Default climb speed
    };
  }

  /**
   * Calculates movement restrictions based on conditions and abilities.
   * @param {Object} tokenData - Token data
   * @returns {Array} Array of movement restrictions
   */
  calculateMovementRestrictions(tokenData) {
    const restrictions = [];
    const conditions = tokenData.conditions || [];

    if (conditions.includes('grappled')) {
      restrictions.push('Cannot move (grappled)');
    }
    if (conditions.includes('restrained')) {
      restrictions.push('Cannot move (restrained)');
    }
    if (conditions.includes('prone')) {
      restrictions.push('Movement costs double (prone)');
    }
    if (conditions.includes('exhaustion')) {
      // Exhaustion levels would need to be tracked separately
      restrictions.push('Speed reduced by exhaustion');
    }

    return restrictions;
  }

  /**
   * Ends the current turn and cleans up movement data.
   * @param {string} tokenId - Token whose turn is ending
   */
  endTurn(tokenId) {
    if (this.currentTurn === tokenId) {
      this.currentTurn = null;
    }

    // Keep turn data for combat log, but mark as completed
    const turnData = this.turnData.get(tokenId);
    if (turnData) {
      turnData.completed = true;
    }
  }

  /**
   * Resets all movement data (for new combat encounter).
   */
  reset() {
    this.turnData.clear();
    this.currentTurn = null;
  }
}

// Create singleton instance for use across components
export const movementTracker = new TurnMovementTracker();

/**
 * Utility function to check if a token can enter another token's space.
 * Based on D&D 5e rules for moving through hostile/friendly creature spaces.
 *
 * @param {Object} movingToken - Token attempting to move
 * @param {Object} occupyingToken - Token currently in the space
 * @param {Object} battleState - Current battle state (for ally/enemy determination)
 * @returns {boolean} True if the space can be entered
 */
export function canEnterTokenSpace(movingToken, occupyingToken, battleState) {
  void battleState; // Reserved for encounter-specific relationship overrides.
  // Map assets never block movement
  if (occupyingToken.type === 'map') {
    return true;
  }

  // Same token can't occupy its own space
  if (movingToken.id === occupyingToken.id) {
    return false;
  }

  // Size considerations: smaller creatures can move through larger friendly creatures
  const movingSize = movingToken.size || 1;
  const occupyingSize = occupyingToken.size || 1;

  // Determine if tokens are allies or enemies (simplified - would need battle state)
  const areAllies = determineRelationship(movingToken, occupyingToken);

  if (areAllies) {
    // Can move through friendly creatures of same or larger size
    return occupyingSize >= movingSize;
  } else {
    // Cannot normally move through hostile creature spaces
    // Special abilities might allow this (e.g., Tumble, Overrun)
    return false;
  }
}

/**
 * Determines the relationship between two tokens for movement purposes.
 * @param {Object} token1 - First token
 * @param {Object} token2 - Second token
 * @param {Object} battleState - Battle state with team information
 * @returns {boolean} True if tokens are allies
 */
function determineRelationship(token1, token2) {
  return areCombatAllies(token1, token2);
}
